/**
 * Update Current Case Helper
 *
 * Updates the user's last active case in Firestore.
 * Called when:
 * - User navigates to a case route (via RequireCaseAccess guard)
 * - User switches cases via header dropdown
 * - User creates a new case
 *
 * Updates:
 * - users/{uid}.settings.currentCaseId
 * - users/{uid}.settings.lastActiveCaseAt
 *
 * LAYER 3 SAFETY: Validates case access before writing to prevent invalid pointers.
 */

import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

/**
 * Verify user has access to a case before setting it as current
 * @param uid - User ID
 * @param caseId - Case ID to validate
 * @returns true if user has access, false otherwise
 */
async function verifyCaseAccess(uid: string, caseId: string): Promise<boolean> {
  try {
    const casesRef = collection(db, 'cases');
    const q = query(
      casesRef,
      where('memberIds', 'array-contains', uid)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.some(doc => doc.id === caseId);
  } catch (err) {
    console.error('[updateCurrentCase] Failed to verify case access:', err);
    return false;
  }
}

/**
 * Update the user's current case in Firestore
 * 
 * LAYER 3 SAFETY NET:
 * - Validates case ID is non-empty and reasonable length
 * - Verifies user has access to the case via memberIds query
 * - Only writes to Firestore if validation passes
 * - Prevents invalid pointers that cause onboarding blank screen bug
 * 
 * @param uid - User ID
 * @param caseId - Case ID to set as current
 */
export async function updateCurrentCase(uid: string, caseId: string): Promise<void> {
  if (!uid) {
    console.warn('[updateCurrentCase] Called without uid');
    return;
  }

  if (!caseId) {
    console.warn('[updateCurrentCase] Called without caseId');
    return;
  }

  // LAYER 3: Validate case ID format (allow both prefixed and auto-generated IDs)
  // Legacy cases may not have 'case-' prefix, so we only validate non-empty
  if (!caseId || caseId.length < 10) {
    console.error(`[updateCurrentCase] Invalid case ID format: ${caseId} (too short or empty)`);
    return;
  }

  // LAYER 3: Verify user has access to this case
  const hasAccess = await verifyCaseAccess(uid, caseId);
  if (!hasAccess) {
    console.error(`[updateCurrentCase] User ${uid} does not have access to case ${caseId}, refusing to set as current`);
    // Log as telemetry event for "near miss" tracking
    console.warn('[TELEMETRY] Invalid currentCaseId write blocked - P0 onboarding incident prevented');
    return;
  }

  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(
      userRef,
      {
        settings: {
          currentCaseId: caseId,
          lastActiveCaseAt: serverTimestamp(),
        },
      },
      { merge: true }
    );

    console.log(`[updateCurrentCase] ✅ Validated and updated current case to ${caseId} for user ${uid}`);
  } catch (err) {
    console.error('[updateCurrentCase] Failed to update current case:', err);
    // Don't throw - this is a background operation that shouldn't block navigation
  }
}

/**
 * Clear the user's current case pointer
 * Called when:
 * - Case is deleted
 * - User's access to a case is revoked
 * - Manual cleanup needed
 * 
 * @param uid - User ID
 */
export async function clearCurrentCase(uid: string): Promise<void> {
  if (!uid) {
    console.warn('[clearCurrentCase] Called without uid');
    return;
  }

  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(
      userRef,
      {
        settings: {
          currentCaseId: null,
          lastActiveCaseAt: serverTimestamp(),
        },
      },
      { merge: true }
    );

    console.log(`[clearCurrentCase] Cleared current case for user ${uid}`);
  } catch (err) {
    console.error('[clearCurrentCase] Failed to clear current case:', err);
  }
}
